import { HostAriaLabelMixin, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import '@schmancy/form/fields/input'
import '@schmancy/primitives/icons'
import '@schmancy/primitives/typography'
import { GoogleMapsLoader } from '@schmancy/primitives/map'
import '@schmancy/primitives/option'
import { css, html, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { EMPTY, Subject, from, fromEvent, merge, of } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'

export type SchmancyPlacePickerChangeEvent = CustomEvent<{
	address: string
	lat: number | null
	lng: number | null
	placeId?: string
}>

/**
 * The live per-keystroke value of the address text box — re-dispatched from
 * the internal `<schmancy-input>`'s own `input` event (see `emitInput()`).
 * Unlike `change`, it fires on every keystroke, not just on a settled
 * selection/drag/blur — the event to bind for live host-state sync (e.g.
 * gating a Save button's `disabled`) rather than `change`, which a disabled
 * native `<button>` can strand (disabled refuses the focus/click a
 * blur-gated commit needs).
 */
export type SchmancyPlacePickerInputEvent = CustomEvent<{ value: string }>

/** The live Autocomplete Data API prediction — carries `toPlace()`, which `resolvePlace` calls to fetch the settled place's fields with no separate geocode round trip. */
type GooglePlacePrediction = NonNullable<
	Awaited<
		ReturnType<
			NonNullable<typeof window.google>['maps']['places']['AutocompleteSuggestion']['fetchAutocompleteSuggestions']
		>
	>['suggestions'][number]['placePrediction']
>

type GoogleAutocompleteSessionToken = InstanceType<
	NonNullable<typeof window.google>['maps']['places']['AutocompleteSessionToken']
>

/** Which Autocomplete API this page's Maps API key can actually use. `undefined` until the first predictions fetch settles; every `schmancy-place-picker` instance on the page shares the discovery (one key, one Cloud project) rather than each re-attempting and re-403ing the unavailable endpoint. */
let placesAutocompleteMode: 'new' | 'legacy' | undefined

interface PlacePrediction {
	placeId: string
	description: string
	/** Present only on the current-API path — absent means this prediction came from the legacy `AutocompleteService` fallback, and `resolvePlace` geocodes by `placeId` instead of calling `toPlace().fetchFields()`. */
	prediction?: GooglePlacePrediction
}

interface ResolvedPlace {
	address: string
	lat: number | null
	lng: number | null
	placeId: string | null
}

/**
 * `<schmancy-place-picker>` component
 *
 * A venue/address search box backed by Google Places Autocomplete, paired
 * with a `schmancy-map` preview whose marker the operator can drag (or the
 * map can be clicked) to fine-tune the exact pin. Composes `schmancy-map`
 * (draggable-marker mode) + `GoogleMapsLoader` — it does not load its own
 * copy of the Maps script.
 *
 * Not a form-associated field (no `ElementInternals`, no native form
 * submission): the composite `{ address, lat, lng, placeId }` value this
 * control produces doesn't collapse into the single-string `value` a
 * `<schmancy-form>` field submits, so a consuming form reads it from the
 * `change` event rather than from `FormData` — mirrors `schmancy-image-crop`.
 *
 * Degrades without a key, without network, or on an unauthorized domain: no
 * `apiKey`, a script load failure, or a geocode failure all fall back to a
 * plain accessible text input plus a "map unavailable" placeholder — never
 * a thrown error — and typing there still emits `change` with `lat`/`lng`
 * `null`. A location form built on this control is fully usable with zero
 * map.
 *
 * @element schmancy-place-picker
 * @fires input - `{ value: string }` — the address text box's live per-keystroke
 *   value, forwarded from the internal `<schmancy-input>`. `composed: true`.
 *   Bind this for live host-state sync; `change` only settles on selection,
 *   drag, or blur and can strand a consumer that gates a disabled native
 *   `<button>` on it (disabled refuses the focus/click a blur-gated commit
 *   needs).
 * @fires change - `{ address, lat, lng, placeId? }` — on every settled selection, marker drag/click, or manual entry (blur/Enter). `composed: true`.
 *
 * @example
 * <schmancy-place-picker
 *   label="Venue"
 *   .apiKey=${GOOGLE_MAPS_KEY}
 *   @change=${(e) => console.log(e.detail)}
 * ></schmancy-place-picker>
 *
 * @example
 * <!-- Edit mode: hydrate from an existing value -->
 * <schmancy-place-picker
 *   label="Venue"
 *   .apiKey=${GOOGLE_MAPS_KEY}
 *   address="Alexanderplatz, Berlin"
 *   .latitude=${52.5219}
 *   .longitude=${13.4132}
 * ></schmancy-place-picker>
 */
@schmancyElement('schmancy-place-picker')
export class SchmancyPlacePicker extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	/** Google Maps API key. Without one the control degrades immediately to a plain address input — never throws. */
	@property({ type: String }) apiKey: string = ''

	/** Accessible name for the search field. */
	@property({ type: String }) label: string = 'Address'

	@property({ type: String }) placeholder: string = ''

	/** Helper text under the field. The degraded state appends its own "map unavailable" note regardless. */
	@property({ type: String }) hint: string = ''

	@property({ type: Boolean }) required: boolean = false

	@property({ type: String }) name: string = ''

	/** Initial address text — read once, at connect, to hydrate edit mode. */
	@property({ type: String }) address: string = ''

	/** Initial latitude — read once, at connect, alongside `longitude` to hydrate edit mode with an exact pin. */
	@property({ type: Number }) latitude?: number

	/** Initial longitude — read once, at connect. */
	@property({ type: Number }) longitude?: number

	/** Initial place id — read once, at connect. Optional; the control resolves its own on selection/drag. */
	@property({ type: String }) placeId?: string

	@property({ type: Number }) zoom: number = 15

	@state() private query = ''
	@state() private lat: number | null = null
	@state() private lng: number | null = null
	@state() private selectedPlaceId: string | null = null
	@state() private predictions: PlacePrediction[] = []
	@state() private open = false
	/** Index into `predictions` the keyboard has moved to (`-1` = none) — drives `aria-activedescendant` and each option's `selected` highlight; never real DOM focus (see `handleKeyDown`). */
	@state() private activeIndex = -1
	@state() private mapsReady = false
	@state() private mapsFailed = false
	@state() private statusMessage = ''

	private readonly a11yId = `schmancy-place-picker-${Math.random().toString(36).slice(2, 10)}`
	private readonly inputRef = createRef<HTMLElement>()

	/** The last address text that was actually settled (hydrated, selected, dragged, or manually committed) — the baseline `manualCommit$` diffs a blur/Enter against, so a blur with no edit since the last settle isn't re-emitted as a fresh manual entry. */
	private lastSettledQuery = ''

	/** One session per search on the current-API path only — created on the first keystroke's predictions fetch, cleared once `resolvePlace` calls `fetchFields` (the session's billing boundary; reusing a concluded token bills each request separately). The legacy fallback below doesn't use it. */
	private sessionToken?: GoogleAutocompleteSessionToken
	private legacyAutocompleteService?: InstanceType<
		NonNullable<typeof window.google>['maps']['places']['AutocompleteService']
	>
	private geocoder?: InstanceType<NonNullable<typeof window.google>['maps']['Geocoder']>

	private readonly query$ = new Subject<string>()
	private readonly selection$ = new Subject<PlacePrediction>()
	private readonly pinMove$ = new Subject<{ lat: number; lng: number }>()
	private readonly manualCommit$ = new Subject<string>()

	override connectedCallback(): void {
		super.connectedCallback()

		this.query = this.address
		this.lat = this.latitude ?? null
		this.lng = this.longitude ?? null
		this.selectedPlaceId = this.placeId ?? null
		this.lastSettledQuery = this.address

		const load$ = this.apiKey
			? GoogleMapsLoader.load(this.apiKey).pipe(
					tap(() => {
						this.mapsReady = true
					}),
					catchError(err => {
						console.error('schmancy-place-picker: Google Maps failed to load', err)
						this.mapsFailed = true
						return EMPTY
					}),
				)
			: of(null).pipe(
					tap(() => {
						this.mapsFailed = true
					}),
				)

		const predictions$ = this.query$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			switchMap(term =>
				this.mapsReady && term.trim().length >= 2 ? this.fetchPredictions(term) : of([] as PlacePrediction[]),
			),
			tap(predictions => {
				this.predictions = predictions
				this.open = predictions.length > 0
				this.activeIndex = -1
				this.statusMessage =
					predictions.length > 0
						? `${predictions.length} suggestion${predictions.length === 1 ? '' : 's'} available.`
						: this.query.trim().length >= 2
							? 'No suggestions found.'
							: ''
			}),
		)

		const selected$ = this.selection$.pipe(
			switchMap(prediction => this.resolvePlace(prediction)),
			tap(place => this.settle(place)),
		)

		const dragged$ = this.pinMove$.pipe(
			switchMap(coords => this.reverseGeocode(coords)),
			tap(place => this.settle(place)),
		)

		const committed$ = this.manualCommit$.pipe(
			filter(value => value !== this.lastSettledQuery),
			tap(value => {
				this.lastSettledQuery = value
				this.query = value
				this.lat = null
				this.lng = null
				this.selectedPlaceId = null
				this.open = false
				this.emitChange()
			}),
		)

		// Capture phase, on the host: fires before schmancy-input's own internal
		// keydown handling (bound to the native <input>, which is deeper along
		// the same path and would otherwise always run first — blurring and
		// committing the raw text before a bubble-phase handler ever saw the
		// keystroke). stopPropagation() in handleKeyDown for the keys it owns
		// (Escape/ArrowDown/ArrowUp/Enter-on-an-active-option) stops the event
		// from ever reaching that inner handler.
		const keydown$ = fromEvent<KeyboardEvent>(this, 'keydown', { capture: true }).pipe(
			tap(event => this.handleKeyDown(event)),
		)

		merge(load$, predictions$, selected$, dragged$, committed$, keydown$)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	/**
	 * `AutocompleteSuggestion.fetchAutocompleteSuggestions` — the replacement
	 * for the retired `AutocompleteService.getPlacePredictions` (unavailable
	 * to any Cloud project created after 2025-03-01). One session token
	 * covers every keystroke of a search; `resolvePlace` clears it once the
	 * session concludes.
	 *
	 * Some pre-existing Cloud projects still haven't enabled "Places API
	 * (New)" and 403 on this call — the first attempt's failure latches
	 * `placesAutocompleteMode` to `'legacy'` for the rest of the page (every
	 * `schmancy-place-picker` instance shares one Maps key/Cloud project), so
	 * every keystroke after that goes straight to the legacy fallback instead
	 * of re-attempting and re-403ing the unavailable endpoint.
	 */
	private fetchPredictions(term: string) {
		if (placesAutocompleteMode === 'legacy') return this.fetchLegacyPredictions(term)

		this.sessionToken ??= new window.google!.maps.places.AutocompleteSessionToken()
		return from(
			window.google!.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
				input: term,
				sessionToken: this.sessionToken,
			}),
		).pipe(
			map(({ suggestions }) => {
				placesAutocompleteMode = 'new'
				return suggestions.flatMap(s =>
					s.placePrediction
						? [
								{
									placeId: s.placePrediction.placeId,
									description: s.placePrediction.text.text,
									prediction: s.placePrediction,
								},
							]
						: [],
				)
			}),
			catchError(() => {
				console.warn(
					'schmancy-place-picker: Autocomplete Data API unavailable on this Cloud project (Places API (New) not enabled) — falling back to the legacy Autocomplete Service for this session.',
				)
				placesAutocompleteMode = 'legacy'
				this.sessionToken = undefined
				return this.fetchLegacyPredictions(term)
			}),
		)
	}

	private fetchLegacyPredictions(term: string) {
		this.legacyAutocompleteService ??= new window.google!.maps.places.AutocompleteService()
		return from(
			new Promise<PlacePrediction[]>(resolve => {
				this.legacyAutocompleteService!.getPlacePredictions({ input: term }, (results, status) => {
					if (status === window.google!.maps.places.PlacesServiceStatus.OK && results) {
						resolve(results.map(r => ({ placeId: r.place_id, description: r.description })))
					} else {
						resolve([])
					}
				})
			}),
		).pipe(catchError(() => of([] as PlacePrediction[])))
	}

	/**
	 * A prediction carrying `prediction` (the current-API path) resolves via
	 * `PlacePrediction.toPlace().fetchFields()` — the settled selection
	 * already carries a `placeId`, so this is a direct place-details fetch,
	 * never a geocode of arbitrary text. A prediction with no `prediction`
	 * (the legacy fallback) resolves the same way the pre-migration code did:
	 * `Geocoder.geocode({ placeId })` — `Place.fetchFields` would 403 the
	 * same way `fetchAutocompleteSuggestions` did on this Cloud project.
	 */
	private resolvePlace(prediction: PlacePrediction) {
		if (prediction.prediction) {
			return from(prediction.prediction.toPlace().fetchFields({ fields: ['formattedAddress', 'location', 'id'] })).pipe(
				map(({ place }): ResolvedPlace => ({
					address: place.formattedAddress ?? prediction.description,
					lat: place.location?.lat() ?? null,
					lng: place.location?.lng() ?? null,
					placeId: place.id ?? prediction.placeId,
				})),
				tap(() => {
					// The session concludes at the first fetchFields call — a fresh token covers the next search.
					this.sessionToken = undefined
				}),
				catchError(() => of({ address: prediction.description, lat: null, lng: null, placeId: prediction.placeId })),
			)
		}

		if (!this.geocoder) {
			this.geocoder = new window.google!.maps.Geocoder()
		}
		return from(
			new Promise<ResolvedPlace>(resolve => {
				this.geocoder!.geocode({ placeId: prediction.placeId }, (results, status) => {
					if (status === 'OK' && results?.[0]) {
						const location = results[0].geometry.location
						resolve({
							address: results[0].formatted_address ?? prediction.description,
							lat: location.lat(),
							lng: location.lng(),
							placeId: prediction.placeId,
						})
					} else {
						resolve({ address: prediction.description, lat: null, lng: null, placeId: prediction.placeId })
					}
				})
			}),
		).pipe(catchError(() => of({ address: prediction.description, lat: null, lng: null, placeId: prediction.placeId })))
	}

	private reverseGeocode(coords: { lat: number; lng: number }) {
		if (!this.geocoder) {
			this.geocoder = new window.google!.maps.Geocoder()
		}
		return from(
			new Promise<ResolvedPlace>(resolve => {
				this.geocoder!.geocode({ location: coords }, (results, status) => {
					if (status === 'OK' && results?.[0]) {
						resolve({
							address: results[0].formatted_address ?? this.query,
							lat: coords.lat,
							lng: coords.lng,
							placeId: results[0].place_id ?? null,
						})
					} else {
						resolve({ address: this.query, lat: coords.lat, lng: coords.lng, placeId: null })
					}
				})
			}),
		).pipe(catchError(() => of({ address: this.query, lat: coords.lat, lng: coords.lng, placeId: null })))
	}

	private settle(place: ResolvedPlace) {
		this.lastSettledQuery = place.address
		this.query = place.address
		this.lat = place.lat
		this.lng = place.lng
		this.selectedPlaceId = place.placeId
		this.open = false
		this.predictions = []
		this.activeIndex = -1
		this.statusMessage = `Selected ${place.address}`
		this.emitChange()
	}

	private emitInput() {
		this.dispatchEvent(
			new CustomEvent<SchmancyPlacePickerInputEvent['detail']>('input', {
				detail: { value: this.query },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private emitChange() {
		const detail: SchmancyPlacePickerChangeEvent['detail'] = { address: this.query, lat: this.lat, lng: this.lng }
		if (this.selectedPlaceId) detail.placeId = this.selectedPlaceId
		this.dispatchEvent(
			new CustomEvent<SchmancyPlacePickerChangeEvent['detail']>('change', {
				detail,
				bubbles: true,
				composed: true,
			}),
		)
	}

	/**
	 * Keyboard focus never leaves the input (a `role="combobox"` with a
	 * popup listbox keeps real DOM focus on the textbox — `aria-activedescendant`
	 * plus each option's `selected` highlight carry the "active option" instead,
	 * mirroring `schmancy-select`'s own combobox). Moving literal focus onto a
	 * `schmancy-option` would blur the input first, and `schmancy-input` treats
	 * its own blur as a manual-commit of whatever text is currently typed —
	 * exactly the premature commit this virtual-focus approach avoids.
	 */
	private handleKeyDown(event: KeyboardEvent) {
		if (!this.open) {
			if (event.key === 'ArrowDown' && this.predictions.length > 0) {
				event.preventDefault()
				this.activeIndex = 0
				this.open = true
			}
			return
		}

		switch (event.key) {
			case 'Escape':
				event.preventDefault()
				event.stopPropagation()
				this.open = false
				this.activeIndex = -1
				break
			case 'ArrowDown':
				event.preventDefault()
				event.stopPropagation()
				this.activeIndex = this.activeIndex < this.predictions.length - 1 ? this.activeIndex + 1 : 0
				break
			case 'ArrowUp':
				event.preventDefault()
				event.stopPropagation()
				this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : this.predictions.length - 1
				break
			case 'Enter':
				// No active option (listbox open, nothing arrowed-to yet): let the
				// event through — schmancy-input's own Enter handling commits the
				// typed text as a manual entry, same as today.
				if (this.activeIndex >= 0 && this.predictions[this.activeIndex]) {
					event.preventDefault()
					event.stopPropagation()
					this.selection$.next(this.predictions[this.activeIndex])
				}
				break
			case 'Tab':
				this.open = false
				this.activeIndex = -1
				break
		}
	}

	protected override render() {
		const listboxId = `${this.a11yId}-listbox`
		const name = this.name || this.label.toLowerCase().replace(/\s+/g, '-')
		const degradedHint = 'Map preview unavailable — the address above is still saved as typed.'

		return html`
			<div class="flex flex-col gap-3">
				<div role="status" aria-live="polite" class="sr-only">${this.statusMessage}</div>

				<div class="relative">
					${when(
						this.mapsFailed,
						() => html`
							<schmancy-input
								${ref(this.inputRef)}
								.ariaLabel=${this.ariaLabel}
								.label=${this.label}
								.name=${name}
								.placeholder=${this.placeholder}
								.required=${this.required}
								.hint=${this.hint || degradedHint}
								.value=${this.query}
								type="text"
								autocomplete="off"
								@input=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.query = e.detail.value
									this.emitInput()
								}}
								@change=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.manualCommit$.next(e.detail.value)
								}}
								@enter=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.manualCommit$.next(e.detail.value)
								}}
							></schmancy-input>
						`,
						() => html`
							<schmancy-input
								${ref(this.inputRef)}
								role="combobox"
								aria-autocomplete="list"
								aria-haspopup="listbox"
								aria-controls=${listboxId}
								aria-expanded=${this.open}
								aria-activedescendant=${this.activeIndex >= 0 ? `${listboxId}-option-${this.activeIndex}` : nothing}
								.ariaLabel=${this.ariaLabel}
								.label=${this.label}
								.name=${name}
								.placeholder=${this.placeholder}
								.required=${this.required}
								.hint=${this.hint}
								.value=${this.query}
								type="text"
								autocomplete="off"
								@input=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.query = e.detail.value
									this.query$.next(e.detail.value)
									this.emitInput()
								}}
								@focus=${() => {
									if (this.predictions.length > 0) this.open = true
								}}
								@change=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.open = false
									this.manualCommit$.next(e.detail.value)
								}}
								@enter=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									this.open = false
									this.manualCommit$.next(e.detail.value)
								}}
							></schmancy-input>

							${when(
								this.open && this.predictions.length > 0,
								() => html`
									<ul
										id=${listboxId}
										role="listbox"
										aria-label="${this.label} suggestions"
										class="absolute z-[1000] mt-1 max-h-72 w-full overflow-auto rounded-lg bg-surface-low shadow-md"
										@mousedown=${(e: Event) => e.preventDefault()}
									>
										${repeat(
											this.predictions,
											p => p.placeId,
											(p, i) => html`
												<schmancy-option
													id="${listboxId}-option-${i}"
													.value=${p.placeId}
													.label=${p.description}
													.selected=${i === this.activeIndex}
													@option-select=${() => this.selection$.next(p)}
												></schmancy-option>
											`,
										)}
									</ul>
								`,
							)}
						`,
					)}
				</div>

				${when(
					this.mapsFailed,
					() => html`
						<div
							class="flex items-center gap-3 rounded-2xl border-2 border-dashed border-control-border bg-surface-container px-4 py-6"
						>
							<schmancy-icon class="text-3xl text-surface-onVariant" aria-hidden="true">map</schmancy-icon>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${degradedHint}
							</schmancy-typography>
						</div>
					`,
					() => {
						const lat = this.lat
						const lng = this.lng
						return when(
							lat !== null && lng !== null,
							() => html`
								<schmancy-map
									pickable
									.apiKey=${this.apiKey}
									.latitude=${lat ?? undefined}
									.longitude=${lng ?? undefined}
									.zoom=${this.zoom}
									.markerTitle=${this.query}
									height="240px"
									@pin-change=${(e: CustomEvent<{ lat: number; lng: number }>) => this.pinMove$.next(e.detail)}
								></schmancy-map>
							`,
							() => html`
								<div
									class="flex items-center gap-3 rounded-2xl border-2 border-dashed border-control-border bg-surface-container px-4 py-6"
								>
									<schmancy-icon class="text-3xl text-surface-onVariant" aria-hidden="true">pin_drop</schmancy-icon>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										Search above to preview the location on the map.
									</schmancy-typography>
								</div>
							`,
						)
					},
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-place-picker': SchmancyPlacePicker
	}
}
